// src/components/OfflineIndicator.jsx
// Shows a subtle banner when the user is offline
import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Show "reconnected" message briefly
        setShowReconnected(true);
        setTimeout(() => setShowReconnected(false), 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  // Reconnected message (shows briefly after coming back online)
  if (showReconnected) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-green-600 text-white px-4 py-2 text-center text-sm font-medium animate-in slide-in-from-bottom duration-200"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center justify-center gap-2">
          <RefreshCw className="w-4 h-4" />
          <span>Back online</span>
        </div>
      </div>
    );
  }

  // Offline indicator
  if (!isOnline) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-amber-600 text-white px-4 py-2 text-center text-sm font-medium animate-in slide-in-from-bottom duration-200"
        role="alert"
        aria-live="assertive"
      >
        <div className="flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          <span>You're offline. Some features may be unavailable.</span>
        </div>
      </div>
    );
  }

  return null;
};

export default OfflineIndicator;
